import moment from "moment";

type State = {
  aspect: string[];
  calling_device_id_list: string[];
  route_device_id_list: string[];
  device_id: string;
  gear_type: string;
};
type PacketData = Record<string, [{ ts: string; value: string }]>;
type ParsedData = Record<string, PacketData>;
type PopulateLogicType = {
  divide_by?: number;
  unit?: string;
  show_time_on_hover?: boolean;
  aspect?: string[];
  route_index?: number;
  total_route?: number;
  fill_color?: string;
  children_key_config?: {
    keys_to_populate?: string[];
    reference_element_id?: string;
    sub_child_class?: string;
    date_element_id?: string;
    device_id_list?: string[];
    with_name?: boolean;
  }[];
};

function populateGroupWiring(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const { aspect } = config;
  console.log("aspect_to_key", aspect, key);
  if (!aspect) return;
  if (aspect.includes(key)) return;

  element.style.display = "none";
}

function showRouteElement(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const { route_index, total_route } = config;

  // total_route can be 0
  if (!route_index || total_route === undefined) return;
  if (route_index > total_route) {
    element.style.display = "none";
  }
}

function getNumericalValue(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  if (!data || !data?.[key] || !data?.[key]?.[0] || !("value" in data?.[key]?.[0]) || data?.[key]?.[0]?.value === "***")
    return null;

  const value = data?.[key]?.[0]?.value;
  const numerical_value = Number(value);
  if (!value || isNaN(numerical_value)) return null;

  return numerical_value;
}

function renderEmptyText(element: HTMLElement) {
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = "-";
}
function populateText(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  if (!data || !data?.[key] || !data?.[key]?.[0] || !("value" in data?.[key]?.[0])) {
    renderEmptyText(element);
    return;
  }
  const value = data?.[key]?.[0]?.value;

  if (value === "***") {
    renderEmptyText(element);
    return;
  }

  const { divide_by = 1, unit = "", show_time_on_hover } = config;
  const numerical_value = Number(value);
  if (!value) return;
  let divided_value;
  if (numerical_value && !isNaN(numerical_value)) {
    divided_value = numerical_value / divide_by + " " + unit;
  } else {
    divided_value = value;
  }
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = divided_value;

  if (show_time_on_hover) {
    showTimeOnHover(element, data, key, config);
  }
}
function showTimeOnHover(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const timestamp = data[key][0].ts;
  if (!timestamp) return;
  const formatted_date_time = moment(Number(timestamp)).format("DD-MM-YYYY HH:mm:ss");
  var titleElement = document.createElementNS("http://www.w3.org/2000/svg", "title");
  titleElement.textContent = `Last Time Captured: ${formatted_date_time}`;
  element.style.cursor = "pointer";
  element.appendChild(titleElement);
}

const voltage_key_set = new Set(["VRG", "VHG", "VHHG", "VDG", "HPR", "DPR", "HHPR", "VS", "VRR"]);
const current_key_set = new Set(["IRG", "IHG", "IHHG", "IDG", "IS"]);

function getParsedValue(value: number, key: string, with_unit: boolean = false) {
  if (voltage_key_set.has(key)) {
    return (value / 1000).toFixed(2) + (with_unit ? " v" : "");
  }

  if (current_key_set.has(key)) {
    return value + (with_unit ? " mA" : "");
  }

  return value;
}

function populateSignalLight(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  if (!data) return;
  const numerical_value = getNumericalValue(element, data, key, config);
  const { fill_color = "white" } = config;
  if (numerical_value && numerical_value > 60) {
    element.style.fill = fill_color;
  } else {
    element.style.fill = "gray";
  }
}

function hideElement(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  element.style.display = "none";
}

function populateMainKeys(element: HTMLElement, data: ParsedData, key: string, config: PopulateLogicType) {
  console.log("data_to_populate", data);
  console.log("element_found", element);
  const { children_key_config } = config;
  if (!children_key_config) return;
  for (const children_key of children_key_config) {
    const {
      keys_to_populate,
      reference_element_id,
      sub_child_class,
      date_element_id,
      device_id_list = [],
      with_name = false,
    } = children_key;

    if (!keys_to_populate || !sub_child_class) continue;
    const final_value = [];

    if (!reference_element_id) continue;

    for (const device_id of device_id_list) {
      const _data = data[device_id];
      for (const populate_key of keys_to_populate) {
        const numerical_value = getNumericalValue(element, _data, populate_key, config);
        const parsed_value = getParsedValue(numerical_value ?? 0, populate_key, true);
        const name = _data?.NAME?.[0]?.value ?? "";
        const _key = with_name && name ? `${name} ${populate_key}` : populate_key;
        final_value.push(`${_key} : ${parsed_value}`);
      }

      const reference_element = document.getElementById(reference_element_id);
      if (!reference_element) continue;
      reference_element.style.visibility = "hidden";
      const textComp = reference_element.querySelector("tspan");

      const start_x_coordinates = textComp?.getAttribute("x");
      const start_y_coordinates = textComp?.getAttribute("y");

      if (!start_x_coordinates || !start_y_coordinates) continue;

      const prev_elements: Element[] = Array.from(element.getElementsByClassName(sub_child_class));

      // if previous elements are not removed then new ones keep overlapping on top
      for (const element of prev_elements) {
        element.remove();
      }

      console.log("final-value", final_value);

      for (let i = 0; i < final_value.length; i++) {
        const value = final_value[i];
        const text_element = document.createElementNS("http://www.w3.org/2000/svg", "text");
        const text_node = document.createTextNode(value);
        text_element.appendChild(text_node);
        element.appendChild(text_element);

        // set required attributes - without them, the element won't get displayed
        text_element.setAttribute("x", start_x_coordinates);
        text_element.setAttribute("y", `${Number(start_y_coordinates) + 20 * i}`);
        text_element.style.fill = "white";
        text_element.style.fontSize = "11";
        text_element.style.letterSpacing = "0em";
        text_element.setAttribute("xml:space", "preserve");

        // set additional attributes
        text_element.setAttribute("class", sub_child_class);
      }

      if (!date_element_id) continue;
      const date_element = document.getElementById(date_element_id);
      if (!date_element) continue;

      populateDateTime(date_element, _data, "DATE", config);
    }
  }
}
function populateDateTime(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const device_key_timestamp = data?.[key]?.[0]?.ts;
  if (!device_key_timestamp || isNaN(Number(device_key_timestamp))) return;
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = moment(Number(device_key_timestamp)).format("DD-MM-YYYY HH:mm:ss");
}

function getConfigFor3AspOr4Asp(params: State) {
  const { device_id, aspect, route_device_id_list, calling_device_id_list } = params;
  console.log("route_device_id_list", route_device_id_list);
  console.log("calling_device_id_list", calling_device_id_list);
  console.log("aspect", aspect);

  const main_keys = [];
  for (const sub_aspect of aspect) {
    main_keys.push(`V${sub_aspect}`);
    main_keys.push(`I${sub_aspect}`);
  }

  const config: Record<string, any> = {
    HHG_Wiring: {
      key: "HHG",
      device_id,
      aspect,
      populate_element: populateGroupWiring,
    },
    DG_Wiring: {
      key: "DG",
      device_id,
      aspect,
      populate_element: populateGroupWiring,
    },
    RG_Wiring: {
      key: "RG",
      device_id,
      aspect,
      populate_element: populateGroupWiring,
    },
    HHG_Aspect: {
      key: "IHHG",
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: ["IDG", "IRG"],
      fill_color: "yellow",
    },
    HG_Aspect: {
      key: "IHG",
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: ["IDG", "IRG"],
      fill_color: "yellow",
    },
    DG_Aspect: {
      key: "IDG",
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: ["IRG", "IHG", "IHHG"],
      fill_color: "green",
    },
    Calling_HG: {
      key: "IS",
      // always assuming only one calling device will be connected to main signal
      device_id: calling_device_id_list?.[0],
      populate_element: populateSignalLight,
      fill_color: "white",
    },
    RG_Aspect: {
      key: "IRG",
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: ["IDG", "IHG", "IHHG"],
      fill_color: "red",
    },
    SG_NAME: {
      key: "NAME",
      device_id,
      populate_element: populateText,
    },
    LOC_NAME: {
      key: "LOC",
      device_id,
      populate_element: populateText,
    },
    "Group 162": {
      // device_id,
      populate_element: populateMainKeys,
      children_key_config: [
        {
          keys_to_populate: main_keys,
          reference_element_id: "VRG",
          sub_child_class: "signal-main_key-and-value",
          date_element_id: "ASPECT_TIME",
          device_id_list: [device_id],
        },
        {
          keys_to_populate: ["HPR", "DPR", "HHPR"],
          reference_element_id: "HPR_3",
          sub_child_class: "signal-relay_key-and-value",
          date_element_id: "PROVING_RELAY_TIME",
          device_id_list: [device_id],
        },
        {
          keys_to_populate: ["VS", "IS", "VRR"],
          reference_element_id: "BUG_V",
          sub_child_class: "signal-route_key-and-value",
          date_element_id: "ROOT_TIME",
          device_id_list: route_device_id_list,
          with_name: true,
        },
        {
          keys_to_populate: ["VS", "IS", "VRR"],
          reference_element_id: "VHHG",
          sub_child_class: "signal-calling_key-and-value",
          date_element_id: "ASPECT_TIME_2",
          device_id_list: calling_device_id_list,
          with_name: true,
        },
      ],
    },
  };

  for (let i = 1; i <= 8; i++) {
    const key = "Route_" + i;
    config[key] = {
      route_index: i,
      total_route: route_device_id_list.length,
      populate_element: showRouteElement,
    };
  }

  if (!route_device_id_list?.length) {
    config["BCDEUG"] = {
      populate_element: hideElement,
    };

    config["Ellipse 159"] = {
      populate_element: hideElement,
    };
  }

  return config;
}

const SIGNAL_ASPECT_CONFIG = {
  DG: {
    current_key: "IDG",
    keys_to_avoid: ["IRG", "IHG", "IHHG"],
    fill_color: "green",
  },
  HG: {
    current_key: "IHG",
    keys_to_avoid: ["IRG", "IDG"],
    fill_color: "yellow",
  },
  HHG: {
    current_key: "IHHG",
    keys_to_avoid: ["IRG", "IDG"],
    fill_color: "yellow",
  },
  RG: {
    current_key: "IRG",
    keys_to_avoid: ["IHG", "IDG", "IHHG"],
    fill_color: "red",
  },
};

function getConfigFor2Asp(params: State) {
  const { device_id, aspect } = params;

  console.log("aspect", aspect);

  const main_keys = [];
  for (const sub_aspect of aspect) {
    main_keys.push(`V${sub_aspect}`);
    main_keys.push(`I${sub_aspect}`);
  }

  const secondary_aspect = aspect.includes("DG") ? "DG" : "HG";

  const config: Record<string, any> = {
    SG_NAME: {
      key: "NAME",
      device_id,
      populate_element: populateText,
    },
    LOC_NAME: {
      key: "LOC",
      device_id,
      populate_element: populateText,
    },
    SG_RG: {
      key: "IRG",
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: ["IDG", "IHG"],
      fill_color: "red",
    },
    SG_DG: {
      key: SIGNAL_ASPECT_CONFIG[secondary_aspect].current_key,
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: SIGNAL_ASPECT_CONFIG[secondary_aspect].keys_to_avoid,
      fill_color: SIGNAL_ASPECT_CONFIG[secondary_aspect].fill_color,
    },
    DECR_RGDG: {
      populate_element: populateGroupWiring,
      key: "DG",
      aspect,
    },
    HECR_RGDG: {
      populate_element: populateGroupWiring,
      key: "HG",
      aspect,
    },
    DG: {
      populate_element: populateGroupWiring,
      key: "DG",
      aspect,
    },
    HG: {
      populate_element: populateGroupWiring,
      key: "HG",
      aspect,
    },
    "Frame 6": {
      populate_element: populateMainKeys,
      children_key_config: [
        {
          keys_to_populate: main_keys,
          reference_element_id: "VRG",
          sub_child_class: "signal-main_key-and-value",
          date_element_id: "ASPECT_TIME",
          device_id_list: [device_id],
        },
        {
          keys_to_populate: ["HPR", "DPR", "HHPR"],
          reference_element_id: "DPR_3",
          sub_child_class: "signal-relay_key-and-value",
          date_element_id: "PROVING_RELAY_TIME",
          device_id_list: [device_id],
        },
      ],
    },
  };

  return config;
}

function getConfigFor1Asp(params: State) {
  const { device_id, aspect } = params;

  console.log("aspect", aspect);

  const main_keys = [];
  for (const sub_aspect of aspect) {
    main_keys.push(`V${sub_aspect}`);
    main_keys.push(`I${sub_aspect}`);
  }

  const aspect_key = aspect.includes("RG") ? "RG" : "HG";

  const config: Record<string, any> = {
    SG_NAME: {
      key: "NAME",
      device_id,
      populate_element: populateText,
    },
    LOC_NAME: {
      key: "LOC",
      device_id,
      populate_element: populateText,
    },
    RG_Signal: {
      populate_element: populateGroupWiring,
      key: "RG",
      aspect,
    },
    CALLING_SIGNAL: {
      populate_element: populateGroupWiring,
      key: "HG",
      aspect,
    },
    "C-HG/RG": {
      populate_element: populateMainKeys,
      children_key_config: [
        {
          keys_to_populate: main_keys,
          reference_element_id: "DPR",
          sub_child_class: "signal-main_key-and-value",
          date_element_id: "Calling_Signl_Time",
          device_id_list: [device_id],
        },
        {
          keys_to_populate: ["HPR", "DPR", "HHPR"],
          reference_element_id: "VRG",
          sub_child_class: "signal-relay_key-and-value",
          date_element_id: "PROVING_RELAY_TIME",
          device_id_list: [device_id],
        },
      ],
    },
    aSPECT_CURRENT: {
      key: SIGNAL_ASPECT_CONFIG[aspect_key].current_key,
      device_id,
      populate_element: populateSignalLight,
      keys_to_avoid: SIGNAL_ASPECT_CONFIG[aspect_key].keys_to_avoid,
      fill_color: SIGNAL_ASPECT_CONFIG[aspect_key].fill_color,
    },
  };

  return config;
}
export function getMainSignalDataMap(params: State): Record<string, any> {
  const { aspect } = params;

  switch (aspect.length) {
    case 1:
      return getConfigFor1Asp(params);
    case 2:
      return getConfigFor2Asp(params);
    case 3:
      return getConfigFor3AspOr4Asp(params);
    case 4:
      return getConfigFor3AspOr4Asp(params);
    default:
      return getConfigFor3AspOr4Asp(params);
  }
}
